import { gql } from '@apollo/client';

export const JobSlimFields = gql`
  fragment JobSlimFields on Job {
    id
    tenantId
    parentId
    clientId
    siteId
    site {
      id
      name
      address {
        addressFriendly
      }
    }
    userId
    startDate
    endDate
    scheduleStartDate
    scheduleEndDate
    title
    description
    itemStatus
    createdAt
    userAcceptedAt
    workType
    addressHash
    applicationsCloseAt
    shiftDurationMinutes
    paidBreakDurationMinutes
    unpaidBreakDurationMinutes
    clockInTime
    clockOutTime
    budget
    classificationId
    allowQuotes
    durationInDays
    workersShouldApplyToJob
    applicationCount
    startType
    startBeforeDate
    startAfterDate
    frequency
    days {
      days
      startTime
      endTime
    }
    postedBy {
      id
      firstName
      lastName
      role
      email
    }
    classification {
      id
      name
      description
      classificationGroupId
    }
    address {
      addressLine1
      addressLine2
      suburb
      state
      postcode
      countryLabel
      countryValue
      addressFriendly
    }
    latLng {
      lat
      lng
    }
    timezone
    customAttributes {
      ref
      value
    }
    marginPercentage
  }
`;

export const JobCoreFields = gql`
  fragment JobCoreFields on Job {
    id
    tenantId
    parentId
    clientId
    siteId
    site {
      id
      name
      address {
        addressFriendly
      }
    }
    userId
    startDate
    endDate
    scheduleStartDate
    scheduleEndDate
    title
    description
    itemStatus
    createdAt
    userAcceptedAt
    workType
    addressHash
    applicationsCloseAt
    shiftDurationMinutes
    paidBreakDurationMinutes
    unpaidBreakDurationMinutes
    clockInTime
    clockOutTime
    budget
    classificationId
    allowQuotes
    durationInDays
    workersShouldApplyToJob
    applicationCount
    startType
    startBeforeDate
    startAfterDate
    frequency
    days {
      days
      startTime
      endTime
    }
    postedBy {
      id
      firstName
      lastName
      role
      email
    }
    classification {
      id
      name
      description
      classificationGroupId
    }
    address {
      addressLine1
      addressLine2
      suburb
      state
      postcode
      countryLabel
      countryValue
      addressFriendly
    }
    latLng {
      lat
      lng
    }
    timezone
    customAttributes {
      ref
      value
    }
    finalClientPayRateCents
    finalWorkerPayRateCents
    marginPercentage
    potentialPayRates {
      potentialWorkerPayRateFloorInCents
      potentialWorkerPayRateCeilingInCents
      potentialWorkerPayRateMedianInCents
      potentialWorkerPayRateAverageInCents
      potentialWorkerPayRateForUserIdInCents
      potentialClientPayRateFloorInCents
      potentialClientPayRateCeilingInCents
      potentialClientPayRateMedianInCents
      potentialClientPayRateAverageInCents
    }
    cancelledByUserId
    cancelledAt
    cancelReason
    cancelledWhileFilledWorkerId
    previousStartDate
    previousEndDate
    previousDays {
      days
      startTime
      endTime
    }
    previousScheduleStartDate
    previousScheduleEndDate
  }
`;

export const GET_JOB = gql`
  ${JobCoreFields}
  query GetJob($id: String!) {
    getJob(id: $id) {
      ...JobCoreFields
    }
  }
`;

export const GET_ALL_JOBS = gql`
  ${JobCoreFields}
  query GetAllJobs {
    getAllJobs {
      ...JobCoreFields
    }
  }
`;

export const GET_ALL_JOBS_FOR_TENANT = gql`
  ${JobCoreFields}
  query GetAllJobsForTenant(
    $tenantId: String!
    $status: [JobStatus!]
    $limit: Int
    $lastEvaluatedKey: String
  ) {
    getAllJobsForTenant(
      tenantId: $tenantId
      status: $status
      limit: $limit
      lastEvaluatedKey: $lastEvaluatedKey
    ) {
      jobs {
        ...JobCoreFields
      }
      hasMore
      lastEvaluatedKey
    }
  }
`;

export const GET_ALL_JOBS_FOR_TENANT_BY_DATE_RANGE = gql`
  ${JobSlimFields}
  query GetAllJobsForTenantBetweenDates(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $status: [JobStatus!]
    $limit: Int
    $lastEvaluatedKey: String
    $includeApplicationCounts: Boolean
  ) {
    getAllJobsForTenantBetweenDates(
      tenantId: $tenantId
      dateFrom: $dateFrom
      dateTo: $dateTo
      status: $status
      limit: $limit
      lastEvaluatedKey: $lastEvaluatedKey
      includeApplicationCounts: $includeApplicationCounts
    ) {
      jobs {
        ...JobSlimFields
      }
      hasMore
      lastEvaluatedKey
    }
  }
`;

export const GET_ALL_JOBS_FOR_CLIENT = gql`
  ${JobCoreFields}
  query GetAllJobsForClient(
    $clientId: String!
    $limit: Int
    $lastEvaluatedKey: String
  ) {
    getAllJobsForClient(
      clientId: $clientId
      limit: $limit
      lastEvaluatedKey: $lastEvaluatedKey
    ) {
      hasMore
      lastEvaluatedKey
      jobs {
        ...JobCoreFields
      }
    }
  }
`;

export const GET_ALL_JOBS_FOR_CLIENT_BY_DATE_RANGE = gql`
  ${JobCoreFields}
  query GetAllJobsForClientBetweenDates(
    $clientId: String!
    $dateFrom: String!
    $dateTo: String!
    $status: [JobStatus!]
    $limit: Int
    $lastEvaluatedKey: String
  ) {
    getAllJobsForClientBetweenDates(
      clientId: $clientId
      dateFrom: $dateFrom
      dateTo: $dateTo
      status: $status
      limit: $limit
      lastEvaluatedKey: $lastEvaluatedKey
    ) {
      jobs {
        ...JobCoreFields
      }
      hasMore
      lastEvaluatedKey
    }
  }
`;

export const GET_ALL_JOBS_BY_STATUS_FOR_USER_ID = gql`
  ${JobCoreFields}
  query GetAllJobsByStatusForUserId(
    $status: [JobStatus!]
    $userId: String
    $limit: Int
    $lastEvaluatedKey: String
  ) {
    getAllJobsByStatusForUserId(
      userId: $userId
      status: $status
      limit: $limit
      lastEvaluatedKey: $lastEvaluatedKey
    ) {
      hasMore
      lastEvaluatedKey
      jobs {
        ...JobCoreFields
      }
    }
  }
`;

export const GET_ALL_POTENTIAL_JOBS_FOR_USER_ID = gql`
  ${JobCoreFields}
  query GetAllPotentialJobsForUserId($userId: String) {
    getAllPotentialJobsForUserId(userId: $userId) {
      ...JobCoreFields
    }
  }
`;

// Job Mutations
export const CREATE_JOB = gql`
  ${JobCoreFields}
  mutation CreateJob(
    $input: CreateJobInput!
    $questionnaire: QuestionnaireInput
  ) {
    createJob(input: $input, questionnaire: $questionnaire) {
      ...JobCoreFields
    }
  }
`;

export const UPDATE_JOB = gql`
  ${JobCoreFields}
  mutation UpdateJob($id: String!, $input: UpdateJobInput!) {
    updateJob(id: $id, input: $input) {
      ...JobCoreFields
    }
  }
`;

export const DELETE_JOB = gql`
  mutation DeleteJob($id: String!) {
    deleteJob(id: $id)
  }
`;

export const CANCEL_JOB = gql`
  mutation CancelJob($id: String!, $reason: String) {
    cancelJob(id: $id, reason: $reason)
  }
`;

export const RETRIGGER_NOTIFICATIONS_FOR_ACTION = gql`
  mutation RetriggerNotificationsForAction(
    $action: NotificationAction!
    $objectId: String!
  ) {
    retriggerNotificationsForAction(action: $action, objectId: $objectId)
  }
`;

export const ASSIGN_USER = gql`
  mutation AssignUser($id: String!, $userId: String!) {
    assignUser(id: $id, userId: $userId)
  }
`;

export const UNASSIGN_USER = gql`
  ${JobCoreFields}
  mutation UnassignUser($id: String!, $reason: String) {
    unassignWorkerFromJob(id: $id, reason: $reason) {
      ...JobCoreFields
    }
  }
`;

export const ACCEPT_JOB = gql`
  ${JobCoreFields}
  mutation AcceptJob($id: String!) {
    acceptJob(id: $id) {
      ...JobCoreFields
    }
  }
`;

// Return a job application
export const APPLY_FOR_JOB = gql`
  mutation ApplyForJob($id: String!) {
    applyForJob(id: $id) {
      id
      jobId
      userId
      createdAt
      itemStatus
      clientId
      tenantId
    }
  }
`;

// Worker withdraw from an accepted job
export const WITHDRAW_FROM_JOB = gql`
  ${JobCoreFields}
  mutation WithdrawFromJob($id: String!, $reason: String) {
    withdrawFromJob(id: $id, reason: $reason) {
      ...JobCoreFields
    }
  }
`;
